/**
 * Campaign Room — the brand-side view of an agent-run campaign.
 * Stage rail · "Needs you" approval inbox · agent activity · roster · budget.
 * Read-only platform state + the brand's own approval clicks; agents do the rest.
 */
import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Bell, Check, ChevronRight, Megaphone, RefreshCw, X } from 'lucide-react';
import { API_BASE_URL, authHeaders } from '../api/coworkApi';

interface CampaignListItem {
  campaign_id: string;
  name: string;
  status?: string;
  brand_name?: string;
  date_start?: string;
  date_end?: string;
}

interface RoomState {
  campaign: Record<string, unknown> & {
    campaign_id?: string; name?: string; status?: string; brand_name?: string;
    date_start?: string; date_end?: string; budget?: number; currency?: string;
  };
  curation: { curation?: Array<Record<string, unknown>>; curated_influencers?: Array<Record<string, unknown>> } | null;
  collaborations: { collaborations?: Array<Record<string, unknown>> } | null;
  drafts: { drafts?: Array<Record<string, unknown>> } | null;
  agentFeed: Array<{ taskId: string; title: string; agentId: string; status: string; lastEvent: string; at: string }>;
}

const STAGES = ['Setup', 'Brief', 'Creators', 'Content', 'Boost', 'Results'] as const;

function fmtDate(v?: string): string {
  if (!v) return '?';
  const d = new Date(v);
  return isNaN(d.getTime()) ? v.slice(0, 10) : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function stageIndexFor(status?: string): number {
  switch ((status || '').toUpperCase()) {
    case 'DRAFT': return 1;
    case 'PUBLIC_DRAFT': return 2;
    case 'LAUNCHED': return 2;
    case 'FULLY_BOOKED':
    case 'ONGOING': return 3;
    case 'SCREENSHOTS': return 4;
    case 'SUMMARY':
    case 'DONE': return 5;
    default: return 0;
  }
}

const AGENT_LABEL: Record<string, string> = {
  'campaign-manager': 'Saga', 'rex-influencer': 'Rex', 'aria-content': 'Aria',
  'kai-analytics': 'Kai', 'leo-perf': 'Lena', 'jones-artdir': 'Jones',
  'account-manager': 'Lykke', 'nova-strategy': 'Nova', 'maya-seo': 'Joanya',
};

export function CampaignRoomPage({ onBack }: { onBack: () => void }) {
  const [campaigns, setCampaigns] = useState<CampaignListItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [room, setRoom] = useState<RoomState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState<string | null>(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/v1/campaign-room`, { headers: authHeaders(false) });
      if (res.status === 409) {
        setError('The Campaign Room needs a Beatly platform session. Sign in via your Beatly handoff link.');
        return;
      }
      if (!res.ok) throw new Error(`Failed to load campaigns (${res.status})`);
      const data = await res.json();
      setCampaigns((data.campaigns || []) as CampaignListItem[]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRoom = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/v1/campaign-room/${id}`, { headers: authHeaders(false) });
      if (!res.ok) throw new Error(`Failed to load campaign (${res.status})`);
      setRoom(await res.json() as RoomState);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { if (selectedId) loadRoom(selectedId); }, [selectedId, loadRoom]);

  const act = async (path: string, key: string) => {
    setActing(key);
    try {
      const res = await fetch(`${API_BASE_URL}/v1/campaign-room/${path}`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({}),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error((d as { message?: string }).message || `Action failed (${res.status})`);
      }
      if (selectedId) await loadRoom(selectedId);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setActing(null);
    }
  };

  // ── Campaign list view ─────────────────────────────────────────
  if (!selectedId) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-8">
        <button onClick={onBack} className="flex items-center gap-1.5 text-[13px] text-[#667085] hover:text-[#344054] mb-5">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-center gap-3 mb-1">
          <Megaphone className="w-6 h-6 text-[#be43ff]" />
          <h1 className="text-[22px] text-[#101828]" style={{ fontWeight: 700 }}>Campaigns</h1>
        </div>
        <p className="text-[13.5px] text-[#667085] mb-6">Agent-run campaigns on the Beatly platform. Open one to see status and what needs you.</p>
        {error && <div className="mb-4 px-4 py-3 rounded-xl bg-[#fef2f2] text-[#b42318] text-[13px]">{error}</div>}
        {loading && <div className="text-[13px] text-[#667085]">Loading campaigns…</div>}
        {!loading && campaigns.length === 0 && !error && (
          <div className="px-4 py-8 rounded-xl border border-gray-200 text-center text-[13.5px] text-[#667085]">
            No campaigns yet: ask Saga to start one.
          </div>
        )}
        <div className="flex flex-col gap-2">
          {campaigns.map(c => (
            <button key={c.campaign_id} onClick={() => setSelectedId(c.campaign_id)}
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl border border-gray-200 bg-white hover:border-[#be43ff]/40 hover:bg-[#faf7fd] text-left transition-colors">
              <div className="flex-1 min-w-0">
                <p className="text-[14px] text-[#101828] truncate" style={{ fontWeight: 600 }}>{c.name}</p>
                <p className="text-[12px] text-[#667085]">{c.brand_name || ''} {c.date_start ? `· ${fmtDate(c.date_start)} → ${fmtDate(c.date_end)}` : ''}</p>
              </div>
              <span className="text-[11px] px-2.5 py-1 rounded-full bg-[#f4eefa] text-[#6d28d9]" style={{ fontWeight: 600 }}>
                {STAGES[stageIndexFor(c.status)]}
              </span>
              <ChevronRight className="w-4 h-4 text-[#98a2b3]" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── Room view ──────────────────────────────────────────────────
  const campaign = room?.campaign;
  const stageIdx = stageIndexFor(campaign?.status as string | undefined);
  const curations = room?.curation?.curation || room?.curation?.curated_influencers || [];
  const pendingCurations = curations.filter(c => String((c as { status?: string }).status || '').toUpperCase() === 'CURATION');
  const drafts = room?.drafts?.drafts || [];
  const pendingDrafts = drafts.filter(d => {
    const s = String((d as { status?: string }).status || '').toUpperCase();
    return s === 'SUBMITTED' || s === 'PENDING' || s === 'IN_REVIEW';
  });
  const collabs = room?.collaborations?.collaborations || [];
  const needsYou = pendingCurations.length + pendingDrafts.length;

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-5">
        <button onClick={() => { setSelectedId(null); setRoom(null); loadList(); }}
          className="flex items-center gap-1.5 text-[13px] text-[#667085] hover:text-[#344054]">
          <ArrowLeft className="w-4 h-4" /> All campaigns
        </button>
        <button onClick={() => selectedId && loadRoom(selectedId)} className="flex items-center gap-1.5 text-[12px] text-[#667085] hover:text-[#344054]">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {error && <div className="mb-4 px-4 py-3 rounded-xl bg-[#fef2f2] text-[#b42318] text-[13px]">{error}</div>}
      {loading && !room && <div className="text-[13px] text-[#667085]">Loading campaign…</div>}

      {campaign && (
        <>
          <div className="mb-4">
            <h1 className="text-[20px] text-[#101828]" style={{ fontWeight: 700 }}>{String(campaign.name || selectedId)}</h1>
            <p className="text-[13px] text-[#667085]">
              {String(campaign.brand_name || '')} · {fmtDate(campaign.date_start)} → {fmtDate(campaign.date_end)}
              {Number.isFinite(Number(campaign.budget)) && Number(campaign.budget) > 0
                ? ` · ${(Number(campaign.budget) / 100).toLocaleString()} ${String(campaign.currency || 'SEK')}`
                : ''}
              {' · run by Saga 🎬'}
            </p>
          </div>

          <div className="flex gap-1.5 mb-6 overflow-x-auto pb-1">
            {STAGES.map((s, i) => (
              <span key={s} className={`text-[11.5px] px-3 py-1.5 rounded-full whitespace-nowrap ${
                i < stageIdx ? 'bg-[#ecfdf3] text-[#067647]'
                : i === stageIdx ? 'bg-[#f4eefa] text-[#6d28d9]'
                : 'bg-[#f9fafb] text-[#98a2b3]'}`}
                style={{ fontWeight: i === stageIdx ? 600 : 500 }}>
                {i < stageIdx ? '✓ ' : i === stageIdx ? '● ' : ''}{s}
              </span>
            ))}
          </div>

          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2.5">
              <Bell className="w-4 h-4 text-[#be43ff]" />
              <h2 className="text-[13px] uppercase tracking-wide text-[#6d28d9]" style={{ fontWeight: 600 }}>
                Needs you ({needsYou})
              </h2>
            </div>
            {needsYou === 0 && (
              <div className="px-4 py-3.5 rounded-xl border border-gray-200 text-[13px] text-[#667085]">
                Nothing right now: the agents have it. You'll see approvals here when they're ready.
              </div>
            )}
            {pendingCurations.map(c => {
              const cid = String((c as { curation_id?: string; collaboration_id?: string }).curation_id || (c as { collaboration_id?: string }).collaboration_id || '');
              const inf = (c as { influencer?: { name?: string; handle?: string; followers_count?: number; engagement_rate?: number; notes?: { note_public?: string } } }).influencer;
              const iname = inf?.name || String((c as { influencer_name?: string; name?: string }).influencer_name || (c as { name?: string }).name || cid);
              return (
                <div key={cid} className="flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-[#be43ff]/40 mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-[13.5px] text-[#101828]" style={{ fontWeight: 600 }}>Approve creator: {iname}</p>
                    <p className="text-[12px] text-[#667085] truncate">
                      {inf?.handle ? `@${inf.handle} · ` : ''}
                      {inf?.followers_count ? `${Number(inf.followers_count).toLocaleString()} followers · ` : ''}
                      {inf?.engagement_rate ? `${(Number(inf.engagement_rate) * 100).toFixed(1)}% ER · ` : ''}
                      {String(inf?.notes?.note_public || (c as { note_public?: string }).note_public || 'see curation notes')}
                    </p>
                  </div>
                  <button disabled={acting === cid} onClick={() => act(`curation/${cid}/accept`, cid)}
                    className="flex items-center gap-1 text-[12px] px-3 py-2 rounded-lg bg-[#101828] text-white disabled:opacity-50" style={{ fontWeight: 600 }}>
                    <Check className="w-3.5 h-3.5" /> Approve
                  </button>
                  <button disabled={acting === cid} onClick={() => act(`curation/${cid}/reject`, cid)}
                    className="flex items-center gap-1 text-[12px] px-3 py-2 rounded-lg border border-gray-200 text-[#667085] disabled:opacity-50">
                    <X className="w-3.5 h-3.5" /> Pass
                  </button>
                </div>
              );
            })}
            {pendingDrafts.map(d => {
              const did = String((d as { draft_id?: string }).draft_id || '');
              return (
                <div key={did} className="flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-[#be43ff]/40 mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-[13.5px] text-[#101828]" style={{ fontWeight: 600 }}>Review draft {did.slice(0, 8)}</p>
                    <p className="text-[12px] text-[#667085]">Submitted by the creator: approve or send back with notes</p>
                  </div>
                  <button disabled={acting === did} onClick={() => act(`draft/${did}/approve`, did)}
                    className="flex items-center gap-1 text-[12px] px-3 py-2 rounded-lg bg-[#101828] text-white disabled:opacity-50" style={{ fontWeight: 600 }}>
                    <Check className="w-3.5 h-3.5" /> Approve
                  </button>
                  <button disabled={acting === did} onClick={() => act(`draft/${did}/reject`, did)}
                    className="flex items-center gap-1 text-[12px] px-3 py-2 rounded-lg border border-gray-200 text-[#667085] disabled:opacity-50">
                    <X className="w-3.5 h-3.5" /> Send back
                  </button>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h2 className="text-[13px] uppercase tracking-wide text-[#98a2b3] mb-2.5" style={{ fontWeight: 600 }}>
                Agents working: no action needed
              </h2>
              <div className="flex flex-col gap-2.5">
                {(room?.agentFeed || []).slice(0, 8).map(f => (
                  <div key={f.taskId} className="text-[12.5px] text-[#475467]">
                    <span style={{ fontWeight: 600 }}>{AGENT_LABEL[f.agentId] || f.agentId}</span>
                    {' — '}{f.lastEvent || f.title}
                    <span className="text-[#98a2b3]"> · {f.status}</span>
                  </div>
                ))}
                {(room?.agentFeed || []).length === 0 && (
                  <p className="text-[12.5px] text-[#98a2b3]">No agent activity yet.</p>
                )}
              </div>
            </div>
            <div>
              <h2 className="text-[13px] uppercase tracking-wide text-[#98a2b3] mb-2.5" style={{ fontWeight: 600 }}>
                Creators ({collabs.length || curations.length})
              </h2>
              <div className="flex flex-col gap-1.5">
                {(collabs.length ? collabs : curations).slice(0, 10).map((c, i) => {
                  const cinf = (c as { influencer?: { name?: string; handle?: string } }).influencer;
                  const nm = cinf?.name || String((c as { influencer_name?: string; name?: string }).influencer_name || (c as { name?: string }).name || `Creator ${i + 1}`);
                  const st = String((c as { status?: string }).status || 'in curation');
                  return (
                    <div key={i} className="flex items-center justify-between text-[12.5px]">
                      <span className="text-[#344054] truncate">{nm}</span>
                      <span className="text-[#98a2b3]">{st.toLowerCase().replace(/_/g, ' ')}</span>
                    </div>
                  );
                })}
                {collabs.length === 0 && curations.length === 0 && (
                  <p className="text-[12.5px] text-[#98a2b3]">Roster appears once curation starts.</p>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
